import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import fr from "@/i18n/locales/fr.json";
import { formatBytes, formatDate } from "@/lib/api";

export const BRAND = "FAB\u2011MIND\u2011OS";
export const nbBrand = (s) => (typeof s === "string" ? s.replace(/FAB-MIND-OS/g, BRAND) : s);

export const LANGS = [
  { code: "fr", label: "Français", locale: "fr-FR" },
  { code: "en", label: "English", locale: "en-GB" },
  { code: "es", label: "Español", locale: "es-ES" },
  { code: "de", label: "Deutsch", locale: "de-DE" },
  { code: "it", label: "Italiano", locale: "it-IT" },
  { code: "pt", label: "Português", locale: "pt-PT" },
  { code: "ar", label: "العربية", locale: "ar" },
  { code: "zh", label: "中文", locale: "zh-CN" },
  { code: "ru", label: "Русский", locale: "ru-RU" },
  { code: "hi", label: "हिन्दी", locale: "hi-IN" },
];
const CODES = LANGS.map((l) => l.code);
const STORAGE_KEY = "fmo_lang";
const loaders = {
  en: () => import("@/i18n/locales/en.json"),
  es: () => import("@/i18n/locales/es.json"),
  de: () => import("@/i18n/locales/de.json"),
  it: () => import("@/i18n/locales/it.json"),
  pt: () => import("@/i18n/locales/pt.json"),
  ar: () => import("@/i18n/locales/ar.json"),
  zh: () => import("@/i18n/locales/zh.json"),
  ru: () => import("@/i18n/locales/ru.json"),
  hi: () => import("@/i18n/locales/hi.json"),
};

function detectLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (CODES.includes(saved)) return saved;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const p of prefs) {
    const code = (p || "").slice(0, 2).toLowerCase();
    if (CODES.includes(code)) return code;
  }
  return "fr";
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);
  const [[dict, dictLang], setLoaded] = useState([fr, "fr"]);

  const [booted, setBooted] = useState(lang === "fr");

  useEffect(() => {
    let alive = true;
    const done = (d) => {
      if (!alive) return;
      setLoaded([d, lang]);
      setBooted(true);
    };
    if (lang === "fr") done(fr);
    else loaders[lang]().catch(() => loaders[lang]()).then((m) => done(m.default || m)).catch(() => alive && setBooted(true));
    const guard = setTimeout(() => alive && setBooted(true), 4000);
    return () => {
      alive = false;
      clearTimeout(guard);
    };
  }, [lang]);

  useEffect(() => {
    document.documentElement.lang = dictLang;
    document.documentElement.dir = dictLang === "ar" ? "rtl" : "ltr";
  }, [dictLang]);

  const setLang = useCallback((code) => {
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  const value = useMemo(() => {
    const locale = LANGS.find((l) => l.code === dictLang)?.locale || "fr-FR";  // number/date format follows the loaded texts
    const t = (key, vars) => {
      let s = dict[key] ?? fr[key] ?? key;
      if (vars) Object.entries(vars).forEach(([k, v]) => (s = s.split(`{${k}}`).join(String(v))));
      return nbBrand(s);
    };
    return {
      lang, setLang, t, locale,
      fmtBytes: (b) => formatBytes(b, (dict["units.bytes"] || fr["units.bytes"]).split(","), locale),
      fmtDate: (iso) => formatDate(iso, locale),
      fmtNum: (n) => new Intl.NumberFormat(locale).format(n),
      fmtDownloads: (n = 0) => t(new Intl.PluralRules(locale).select(n) === "one" ? "apps.download.one" : "apps.download.other", { n: new Intl.NumberFormat(locale).format(n) }),
    };
  }, [lang, dict, dictLang, setLang]);

  return <I18nContext.Provider value={value}>{booted ? children : null}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
